import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Mail, Rocket } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailChange() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmailChange();
    } else {
      setStatus('error');
      setMessage('No verification token provided.');
    }
  }, [token]);

  const verifyEmailChange = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/verify-email-change?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Email changed successfully!');
        setNewEmail(data.new_email);
        toast.success('Email address updated successfully!');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed. The link may be invalid or expired.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage('An error occurred during verification. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Rocket className="h-6 w-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-white">Business Genie</span>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-white">Email Change Verification</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="flex flex-col items-center justify-center py-8">
              {status === 'loading' && (
                <>
                  <Loader2 className="h-16 w-16 animate-spin text-cyan-500 mb-4" />
                  <p className="text-slate-400 text-center">Verifying your new email address...</p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                    <CheckCircle className="h-12 w-12 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Email Updated!</h3>
                  <p className="text-slate-400 text-center mb-2">{message}</p>
                  {newEmail && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 rounded-lg mb-6">
                      <Mail className="h-4 w-4 text-cyan-400" />
                      <span className="text-cyan-300">{newEmail}</span>
                    </div>
                  )}
                  <p className="text-sm text-amber-400 mb-4">
                    Please log in again with your new email address.
                  </p>
                  <Button
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                    onClick={() => navigate('/login')}
                  >
                    Go to Login
                  </Button>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                    <XCircle className="h-12 w-12 text-red-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Verification Failed</h3>
                  <p className="text-slate-400 text-center mb-6">{message}</p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="border-slate-700 text-slate-300 hover:bg-slate-700"
                      onClick={() => navigate('/profile')}
                    >
                      Back to Profile
                    </Button>
                    <Button
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                      onClick={() => navigate('/login')}
                    >
                      Go to Login
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
